import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../components/Header";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { API_BASE_URL } from "../../config/api";

const AddProduct = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [airports, setAirports] = useState([]);
  const [loadingAirports, setLoadingAirports] = useState(true);
  const [terminals, setTerminals] = useState([]);
  const [loadingTerminals, setLoadingTerminals] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // Fetch airports on component mount
  useEffect(() => {
    const fetchAirports = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${API_BASE_URL}/airport/all`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        if (data.subCode === 200 && data.items) {
          setAirports(data.items);
        }
      } catch (err) {
        console.error("Error fetching airports:", err);
        setError("Failed to load airports");
      } finally {
        setLoadingAirports(false);
      }
    };

    fetchAirports();
  }, []);

  // Fetch companies on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${API_BASE_URL}/company/getAll`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        if (data.subCode === 200 && data.items) {
          setCompanies(data.items);
        }
      } catch (err) {
        console.error("Error fetching companies:", err);
        setError("Failed to load companies");
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, []);

  // Fetch terminals for selected airport
  const fetchTerminals = async (airportId) => {
    if (!airportId) {
      setTerminals([]);
      return;
    }

    setLoadingTerminals(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/airport/terminal`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ airportId: airportId }),
      });

      const data = await response.json();
      if (data.subCode === 200 && data.items) {
        setTerminals(data.items);
      } else {
        setTerminals([]);
      }
    } catch (err) {
      console.error("Error fetching terminals:", err);
      setTerminals([]);
    } finally {
      setLoadingTerminals(false);
    }
  };

  const handleFormSubmit = async (values) => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("authToken");
      // Exclude selectedAirportId, selectedTerminalId, and selectedCompanyId from submission as they're only for internal state
      const { selectedAirportId, selectedTerminalId, selectedCompanyId, ...submitValues } = values;
      const response = await fetch(`${API_BASE_URL}/product/add`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submitValues),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to add product");
      }

      setSuccess("Product added successfully!");
      // Optionally reset form or redirect
    } catch (err) {
      console.error("Add product error:", err);
      setError(err.message || "An error occurred while adding the product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box m="20px">
      <Header title="ADD PRODUCT" subtitle="Add a new product to the website" />

      {error && (
        <Box
          color="error.main"
          p={2}
          mb={2}
          sx={{ backgroundColor: "rgba(211, 47, 47, 0.1)", borderRadius: "4px" }}
        >
          {error}
        </Box>
      )}

      {success && (
        <Box
          color="success.main"
          p={2}
          mb={2}
          sx={{ backgroundColor: "rgba(46, 125, 50, 0.1)", borderRadius: "4px" }}
        >
          {success}
        </Box>
      )}

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={productSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Basic Information
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  {/* Company Selection Dropdown */}
                  <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 4" }}>
                    <InputLabel>Select Company</InputLabel>
                    <Select
                      value={values.selectedCompanyId || ""}
                      onChange={(e) => {
                        const selectedCompany = companies.find(
                          (company) => company._id === e.target.value
                        );
                        if (selectedCompany) {
                          setFieldValue("selectedCompanyId", selectedCompany._id);
                          
                          // Extract numeric part from _id for company_id (e.g., "TD087" -> 87, or use hash)
                          let numericId = null;
                          const numericMatch = selectedCompany._id?.match(/\d+/);
                          if (numericMatch) {
                            numericId = parseInt(numericMatch[0], 10);
                          } else {
                            // If no numbers found, create a hash from the string
                            let hash = 0;
                            const idStr = selectedCompany._id || "";
                            for (let i = 0; i < idStr.length; i++) {
                              const char = idStr.charCodeAt(i);
                              hash = ((hash << 5) - hash) + char;
                              hash = hash & hash; // Convert to 32bit integer
                            }
                            numericId = Math.abs(hash) % 100000; // Limit to reasonable number
                          }
                          setFieldValue("company_id", numericId || "");
                          
                          setFieldValue("company_name", selectedCompany.name || "");
                          setFieldValue("company_email", selectedCompany.email || "");
                          setFieldValue("phone", selectedCompany.contact_number || "");
                          setFieldValue("clname", selectedCompany.registered_owner || selectedCompany.name || "");
                          setFieldValue("cemail", selectedCompany.customer_support_email || selectedCompany.email || "");
                          setFieldValue("cnum", selectedCompany.registration_no || "");
                          
                          // Auto-generate SKU based on company data
                          const companyNameAbbr = (selectedCompany.name || "")
                            .split(" ")
                            .map(word => word.charAt(0).toUpperCase())
                            .join("")
                            .substring(0, 3) || "CMP";
                          const timestamp = Date.now().toString().slice(-6);
                          const skuValue = `${companyNameAbbr}-${selectedCompany._id || ""}-${timestamp}`.toUpperCase();
                          setFieldValue("sku", skuValue);
                        }
                      }}
                      label="Select Company"
                      disabled={loadingCompanies}
                    >
                      {loadingCompanies ? (
                        <MenuItem value="">Loading companies...</MenuItem>
                      ) : companies.length === 0 ? (
                        <MenuItem value="">No companies available</MenuItem>
                      ) : (
                        companies.map((company) => (
                          <MenuItem key={company._id} value={company._id}>
                            {company.name} {company._id ? `(ID: ${company._id})` : ""}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Company ID"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.company_id}
                    name="company_id"
                    error={!!touched.company_id && !!errors.company_id}
                    helperText={touched.company_id && errors.company_id}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Company Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.company_name}
                    name="company_name"
                    error={!!touched.company_name && !!errors.company_name}
                    helperText={touched.company_name && errors.company_name}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="email"
                    label="Company Email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.company_email}
                    name="company_email"
                    error={!!touched.company_email && !!errors.company_email}
                    helperText={touched.company_email && errors.company_email}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="SKU"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.sku}
                    name="sku"
                    error={!!touched.sku && !!errors.sku}
                    helperText={touched.sku && errors.sku}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.isactive === 1}
                        onChange={(e) =>
                          setFieldValue("isactive", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="Is Active"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.featured === 1}
                        onChange={(e) =>
                          setFieldValue("featured", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="Featured"
                    sx={{ gridColumn: "span 2" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Contact Information */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Contact Information
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Phone"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.phone}
                    name="phone"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Company Legal Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.clname}
                    name="clname"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="email"
                    label="Company Email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.cemail}
                    name="cemail"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Company Number"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.cnum}
                    name="cnum"
                    sx={{ gridColumn: "span 2" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Valeting & Rating */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Valeting & Rating
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Valeting"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.valeting}
                    name="valeting"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.m_valeting === 1}
                        onChange={(e) =>
                          setFieldValue("m_valeting", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="M Valeting"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.o_valeting === 1}
                        onChange={(e) =>
                          setFieldValue("o_valeting", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="O Valeting"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Rating"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.Rating}
                    name="Rating"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Rating Percentage"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.Ratingpercentage}
                    name="Ratingpercentage"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Company Rating"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.company_rating}
                    name="company_rating"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Company Rating Show"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.company_rating_show}
                    name="company_rating_show"
                    sx={{ gridColumn: "span 1" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Commission & Hours */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Commission & Hours
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Commission"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.commision}
                    name="commision"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Short Hours"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.short_hours}
                    name="short_hours"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Edit Short Hours"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.edit_short_hours}
                    name="edit_short_hours"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Cancel Short Hours"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.cancel_short_hours}
                    name="cancel_short_hours"
                    sx={{ gridColumn: "span 1" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Content */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Content
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box display="flex" flexDirection="column" gap="30px">
                  <TextField
                    fullWidth
                    variant="filled"
                    multiline
                    rows={4}
                    label="Description"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.description}
                    name="description"
                    error={!!touched.description && !!errors.description}
                    helperText={touched.description && errors.description}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    multiline
                    rows={4}
                    label="Terms"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.term}
                    name="term"
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    multiline
                    rows={6}
                    label="Company Info"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.company_info}
                    name="company_info"
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    multiline
                    rows={4}
                    label="On Arrival"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.on_arrival}
                    name="on_arrival"
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    multiline
                    rows={4}
                    label="On Return"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.on_return}
                    name="on_return"
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    multiline
                    rows={3}
                    label="Mail Procedure"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.mail_procedure}
                    name="mail_procedure"
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Map URL"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.map_url}
                    name="map_url"
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Image"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.image}
                    name="image"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Airport & Terminal */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Airport & Terminal
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  {/* Airport Selection Dropdown */}
                  <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 4" }}>
                    <InputLabel>Select Airport</InputLabel>
                    <Select
                      value={values.selectedAirportId || ""}
                      onChange={(e) => {
                        const selectedAirport = airports.find(
                          (airport) => airport.id === e.target.value
                        );
                        if (selectedAirport) {
                          setFieldValue("selectedAirportId", selectedAirport.id);
                          setFieldValue("airportid", selectedAirport.id);
                          setFieldValue("airport_id", selectedAirport.id);
                          setFieldValue("airport_name", selectedAirport.name);
                          // Fetch terminals for selected airport
                          fetchTerminals(selectedAirport.id);
                          // Reset terminal selection when airport changes
                          setFieldValue("selectedTerminalId", "");
                          setFieldValue("terminal", []);
                        }
                      }}
                      label="Select Airport"
                      disabled={loadingAirports}
                    >
                      {loadingAirports ? (
                        <MenuItem value="">Loading airports...</MenuItem>
                      ) : airports.length === 0 ? (
                        <MenuItem value="">No airports available</MenuItem>
                      ) : (
                        airports.map((airport) => (
                          <MenuItem key={airport.id} value={airport.id}>
                            {airport.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>

                  {/* Auto-filled Airport Fields (Read-only) */}
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Airport ID"
                    value={values.airportid || ""}
                    name="airportid"
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Airport Name"
                    value={values.airport_name || ""}
                    name="airport_name"
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Airport ID (duplicate)"
                    value={values.airport_id || ""}
                    name="airport_id"
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Mileage Description"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.millagedescription}
                    name="millagedescription"
                    sx={{ gridColumn: "span 2" }}
                  />

                  {/* Terminal Selection */}
                  <Box sx={{ gridColumn: "span 4" }}>
                    <Typography variant="h6" mb={2}>
                      Terminals
                    </Typography>
                    
                    {/* Terminal Selection Dropdown and Add Button */}
                    <Box display="flex" gap={2} mb={2} alignItems="flex-end">
                      <FormControl fullWidth variant="filled" sx={{ flex: 1 }}>
                        <InputLabel>Select Terminal</InputLabel>
                        <Select
                          value={values.selectedTerminalId || ""}
                          onChange={(e) => {
                            setFieldValue("selectedTerminalId", e.target.value);
                          }}
                          label="Select Terminal"
                          disabled={loadingTerminals || !values.selectedAirportId}
                        >
                          {!values.selectedAirportId ? (
                            <MenuItem value="">Please select an airport first</MenuItem>
                          ) : loadingTerminals ? (
                            <MenuItem value="">Loading terminals...</MenuItem>
                          ) : terminals.length === 0 ? (
                            <MenuItem value="">No terminals available</MenuItem>
                          ) : (
                            terminals
                              .filter(
                                (terminal) =>
                                  !values.terminal.some(
                                    (addedTerminal) => addedTerminal.id === terminal.id
                                  )
                              )
                              .map((terminal) => (
                                <MenuItem key={terminal.id} value={terminal.id}>
                                  {terminal.name}
                                </MenuItem>
                              ))
                          )}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const selectedTerminal = terminals.find(
                            (terminal) => terminal.id === values.selectedTerminalId
                          );
                          if (
                            selectedTerminal &&
                            !values.terminal.some(
                              (term) => term.id === selectedTerminal.id
                            )
                          ) {
                            setFieldValue("terminal", [
                              ...values.terminal,
                              { id: selectedTerminal.id, name: selectedTerminal.name },
                            ]);
                            setFieldValue("selectedTerminalId", "");
                          }
                        }}
                        disabled={
                          !values.selectedTerminalId ||
                          loadingTerminals ||
                          !values.selectedAirportId ||
                          terminals.filter(
                            (terminal) =>
                              !values.terminal.some(
                                (addedTerminal) => addedTerminal.id === terminal.id
                              )
                          ).length === 0
                        }
                      >
                        Add Terminal
                      </Button>
                    </Box>

                    {/* Added Terminals List */}
                    {values.terminal && values.terminal.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" mb={1} color="text.secondary">
                          Added Terminals ({values.terminal.length})
                        </Typography>
                        {values.terminal.map((term, index) => (
                          <Box
                            key={`${term.id}-${index}`}
                            display="flex"
                            gap={2}
                            mb={2}
                            alignItems="center"
                            p={2}
                            sx={{
                              backgroundColor: "rgba(0, 0, 0, 0.02)",
                              borderRadius: "4px",
                              border: "1px solid rgba(0, 0, 0, 0.1)",
                            }}
                          >
                            <TextField
                              variant="filled"
                              type="number"
                              label="Terminal ID"
                              value={term.id}
                              InputProps={{
                                readOnly: true,
                              }}
                              sx={{ flex: 1 }}
                            />
                            <TextField
                              variant="filled"
                              type="text"
                              label="Terminal Name"
                              value={term.name}
                              InputProps={{
                                readOnly: true,
                              }}
                              sx={{ flex: 2 }}
                            />
                            <IconButton
                              color="error"
                              onClick={() => {
                                const newTerminals = values.terminal.filter(
                                  (_, i) => i !== index
                                );
                                setFieldValue("terminal", newTerminals);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Info message when all terminals are added */}
                    {values.selectedAirportId &&
                      terminals.length > 0 &&
                      terminals.filter(
                        (terminal) =>
                          !values.terminal.some(
                            (addedTerminal) => addedTerminal.id === terminal.id
                          )
                      ).length === 0 &&
                      values.terminal.length > 0 && (
                        <Typography variant="body2" color="info.main" mt={1}>
                          All available terminals have been added.
                        </Typography>
                      )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Security Features */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Security Features
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="20px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.Hr_Patrols === 1}
                        onChange={(e) =>
                          setFieldValue("Hr_Patrols", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="HR Patrols"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.cctv === 1}
                        onChange={(e) =>
                          setFieldValue("cctv", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="CCTV"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.fencing === 1}
                        onChange={(e) =>
                          setFieldValue("fencing", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="Fencing"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.keep_your_keys === 1}
                        onChange={(e) =>
                          setFieldValue("keep_your_keys", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="Keep Your Keys"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.security_lighting === 1}
                        onChange={(e) =>
                          setFieldValue("security_lighting", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="Security Lighting"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.secure_barrier === 1}
                        onChange={(e) =>
                          setFieldValue("secure_barrier", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="Secure Barrier"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.patrolled === 1}
                        onChange={(e) =>
                          setFieldValue("patrolled", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="Patrolled"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.park_mark === 1}
                        onChange={(e) =>
                          setFieldValue("park_mark", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="Park Mark"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.disability_friendly === 1}
                        onChange={(e) =>
                          setFieldValue("disability_friendly", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="Disability Friendly"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.bpa_member === 1}
                        onChange={(e) =>
                          setFieldValue("bpa_member", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="BPA Member"
                    sx={{ gridColumn: "span 1" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.buy_with_confidence === 1}
                        onChange={(e) =>
                          setFieldValue("buy_with_confidence", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="Buy With Confidence"
                    sx={{ gridColumn: "span 1" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Booking Settings */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Booking Settings
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                    <InputLabel>Company Type</InputLabel>
                    <Select
                      value={values.company_type}
                      onChange={handleChange}
                      name="company_type"
                    >
                      <MenuItem value={1}>Type 1</MenuItem>
                      <MenuItem value={2}>Type 2</MenuItem>
                      <MenuItem value={3}>Type 3</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                    <InputLabel>Offer Type</InputLabel>
                    <Select
                      value={values.offer_type}
                      onChange={handleChange}
                      name="offer_type"
                    >
                      <MenuItem value={1}>Offer 1</MenuItem>
                      <MenuItem value={2}>Offer 2</MenuItem>
                      <MenuItem value={3}>Offer 3</MenuItem>
                      <MenuItem value={4}>Offer 4</MenuItem>
                      <MenuItem value={5}>Offer 5</MenuItem>
                      <MenuItem value={6}>Offer 6</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Start Time"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.start_time}
                    name="start_time"
                    placeholder="HH:MM"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="End Time"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.end_time}
                    name="end_time"
                    placeholder="HH:MM"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Discount Code"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.discount_code}
                    name="discount_code"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Discount Amount"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.discount_amount}
                    name="discount_amount"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Total Amount"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.total_amount}
                    name="total_amount"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="SMS Confirmation"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.sms_confirmation}
                    name="sms_confirmation"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Cancellation Cover"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.cancellation_cover}
                    name="cancellation_cover"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Booking Fee"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.booking_fee}
                    name="booking_fee"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.is_api === 1}
                        onChange={(e) =>
                          setFieldValue("is_api", e.target.checked ? 1 : 0)
                        }
                      />
                    }
                    label="Is API"
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Sequence"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.sequence}
                    name="sequence"
                    sx={{ gridColumn: "span 2" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Dates & Times */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" fontWeight="600">
                  Dates & Times
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  display="grid"
                  gap="30px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="date"
                    label="Drop Off Date"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.drop_off_date}
                    name="drop_off_date"
                    InputLabelProps={{ shrink: true }}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="time"
                    label="Drop Off Time"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.drop_off_time}
                    name="drop_off_time"
                    InputLabelProps={{ shrink: true }}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="date"
                    label="Pick Up Date"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.pick_up_date}
                    name="pick_up_date"
                    InputLabelProps={{ shrink: true }}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="time"
                    label="Pick Up Time"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.pick_up_time}
                    name="pick_up_time"
                    InputLabelProps={{ shrink: true }}
                    sx={{ gridColumn: "span 2" }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            <Box display="flex" justifyContent="end" mt="20px" gap="10px">
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  // Reset form logic if needed
                }}
              >
                Reset
              </Button>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={loading}
              >
                {loading ? "Adding Product..." : "Add Product"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

// Validation schema
const productSchema = yup.object().shape({
  company_id: yup.number().required("Company ID is required"),
  company_name: yup.string().required("Company name is required"),
  company_email: yup.string().email("Invalid email").required("Company email is required"),
  sku: yup.string().required("SKU is required"),
});

// Initial values
const initialValues = {
  company_id: 110,
  company_name: "",
  company_email: "",
  sku: "",
  isactive: 1,
  featured: 1,
  valeting: 20,
  m_valeting: 1,
  o_valeting: 1,
  Rating: 1,
  commision: 35,
  short_hours: 6,
  phone: "",
  clname: "",
  cemail: "",
  cnum: "",
  description: "",
  term: "",
  company_info: "",
  map_url: "",
  image: "",
  selectedAirportId: "",
  airportid: "",
  millagedescription: "1",
  sequence: 0,
  created_on_date: new Date().toISOString().slice(0, 19).replace("T", " "),
  modify_on_date: new Date().toISOString().slice(0, 19).replace("T", " "),
  created_by_user: 1,
  modify_by_user: 1,
  mail_procedure: "",
  Hr_Patrols: 1,
  cctv: 1,
  fencing: 1,
  keep_your_keys: 0,
  security_lighting: 1,
  secure_barrier: 1,
  patrolled: 1,
  park_mark: 0,
  disability_friendly: 1,
  bpa_member: 1,
  buy_with_confidence: 0,
  edit_short_hours: 24,
  cancel_short_hours: 24,
  company_type: 1,
  offer_type: 6,
  Ratingpercentage: 1,
  start_time: "00:00",
  end_time: "04:00",
  company_rating: 5,
  company_rating_show: 4.5,
  airport_name: "",
  airport_id: "",
  drop_off_date: "",
  drop_off_time: "",
  pick_up_date: "",
  pick_up_time: "",
  phone_number: null,
  discount_code: "",
  discount_amount: 0,
  total_amount: "",
  day_difference: 0,
  plan_id: null,
  sms_confirmation: 0.5,
  cancellation_cover: 2,
  booking_fee: 0.99,
  is_api: 0,
  image_url: null,
  quote_id: null,
  parking_type: null,
  on_arrival: "",
  on_return: "",
  selectedTerminalId: "",
  terminal: [],
  selectedCompanyId: "",
};

export default AddProduct;
